<script setup>
import { reactive, computed, onMounted, ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { useTrack, useAlert } from 'dashboard/composables';
import PlaneAPI from 'dashboard/api/integrations/plane';
import validations from './validations';
import { parsePlaneAPIErrorResponse } from 'dashboard/store/utils/api';
import SearchableDropdown from 'dashboard/components-next/dropdown/SearchableDropdown.vue';
import { PLANE_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  title: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();

const projects = ref([]);
const assignees = ref([]);
const labels = ref([]);
const statuses = ref([]);

const priorities = [
  { id: 'none', name: 'None' },
  { id: 'urgent', name: 'Urgent' },
  { id: 'high', name: 'High' },
  { id: 'medium', name: 'Medium' },
  { id: 'low', name: 'Low' },
];

const isCreating = ref(false);
const inputStyles = { borderRadius: '0.75rem', fontSize: '0.875rem' };

const formState = reactive({
  title: '',
  description: '',
  projectId: '',
  assigneeId: '',
  labelId: '',
  stateId: '',
  priority: '',
});
const v$ = useVuelidate(validations, formState);

const isSubmitDisabled = computed(
  () =>
    v$.value.title.$invalid || v$.value.projectId.$invalid || isCreating.value
);
const nameError = computed(() =>
  v$.value.title.$error
    ? t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.TITLE.REQUIRED_ERROR')
    : ''
);
const projectError = computed(() =>
  v$.value.projectId.$error
    ? t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.PROJECT.REQUIRED_ERROR')
    : ''
);

const dropdowns = computed(() => {
  return [
    {
      type: 'projectId',
      label: 'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.PROJECT.LABEL',
      items: projects.value,
      placeholder: 'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.PROJECT.SEARCH',
      error: projectError.value,
    },
    {
      type: 'assigneeId',
      label: 'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.ASSIGNEE.LABEL',
      items: assignees.value,
      placeholder:
        'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.ASSIGNEE.SEARCH',
      error: '',
    },
    {
      type: 'labelId',
      label: 'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.LABEL.LABEL',
      items: labels.value,
      placeholder: 'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.LABEL.SEARCH',
      error: '',
    },
    {
      type: 'priority',
      label: 'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.PRIORITY.LABEL',
      items: priorities,
      placeholder:
        'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.PRIORITY.SEARCH',
      error: '',
    },
    {
      type: 'stateId',
      label: 'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.STATUS.LABEL',
      items: statuses.value,
      placeholder: 'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.STATUS.SEARCH',
      error: '',
    },
  ];
});

const onClose = () => emit('close');

const getProjects = async () => {
  try {
    const response = await PlaneAPI.getProjects();
    projects.value = response.data;
  } catch (error) {
    const errorMessage = parsePlaneAPIErrorResponse(
      error,
      t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.LOADING_PROJECT_ERROR')
    );
    useAlert(errorMessage);
  }
};

const getProjectEntities = async () => {
  try {
    const response = await PlaneAPI.getProjectEntities(formState.projectId);
    assignees.value = response.data.users;
    labels.value = response.data.labels;
    statuses.value = response.data.states;
  } catch (error) {
    const errorMessage = parsePlaneAPIErrorResponse(
      error,
      t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.LOADING_PROJECT_ENTITIES_ERROR')
    );
    useAlert(errorMessage);
  }
};

const onChange = (item, type) => {
  formState[type] = item.id;
  if (type === 'projectId') {
    formState.assigneeId = '';
    formState.stateId = '';
    formState.labelId = '';
    getProjectEntities();
  }
};

const createIssue = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const payload = {
    project_id: formState.projectId,
    title: formState.title,
    description_html: formState.description || undefined,
    assignee_id: formState.assigneeId || undefined,
    state_id: formState.stateId || undefined,
    priority: formState.priority || undefined,
    label_ids: formState.labelId ? [formState.labelId] : undefined,
    conversation_id: props.conversationId,
  };

  try {
    isCreating.value = true;
    const response = await PlaneAPI.createIssue(payload);
    const { id: issueId, project_id: projectId } = response.data;
    await PlaneAPI.linkIssue({
      conversation_id: props.conversationId,
      project_id: projectId,
      issue_id: issueId,
      title: props.title,
    });
    useAlert(t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.CREATE_SUCCESS'));
    useTrack(PLANE_EVENTS.CREATE_ISSUE);
    onClose();
  } catch (error) {
    const errorMessage = parsePlaneAPIErrorResponse(
      error,
      t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.CREATE_ERROR')
    );
    useAlert(errorMessage);
  } finally {
    isCreating.value = false;
  }
};

onMounted(getProjects);
</script>

<template>
  <div>
    <Input
      v-model="formState.title"
      class="w-full"
      :label="$t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.TITLE.LABEL')"
      :placeholder="
        $t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.TITLE.PLACEHOLDER')
      "
      :message="nameError"
      :message-type="nameError ? 'error' : 'info'"
      @input="v$.title.$touch"
    />
    <label>
      {{ $t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.DESCRIPTION.LABEL') }}
      <textarea
        v-model="formState.description"
        :style="{ ...inputStyles, padding: '0.5rem 0.75rem' }"
        rows="3"
        class="text-sm"
        :placeholder="
          $t(
            'INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.FORM.DESCRIPTION.PLACEHOLDER'
          )
        "
      />
    </label>
    <div class="flex flex-col gap-4">
      <SearchableDropdown
        v-for="dropdown in dropdowns"
        :key="dropdown.type"
        :type="dropdown.type"
        :value="formState[dropdown.type]"
        :label="$t(dropdown.label)"
        :items="dropdown.items"
        :placeholder="$t(dropdown.placeholder)"
        :error="dropdown.error"
        @change="onChange"
      />
    </div>
    <div class="flex items-center justify-end w-full gap-2 mt-8">
      <Button
        faded
        slate
        type="reset"
        :label="$t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.CANCEL')"
        @click.prevent="onClose"
      />
      <Button
        type="submit"
        :label="$t('INTEGRATION_SETTINGS.PLANE.ADD_OR_LINK.CREATE')"
        :disabled="isSubmitDisabled"
        :is-loading="isCreating"
        @click.prevent="createIssue"
      />
    </div>
  </div>
</template>
